import React from 'react'
import IntegrationCard from '@/features/integrations/components/IntegrationCard'
import GoogleWorkspaceConnectModal from './GoogleWorkspaceConnectModal'
import { useGoogleWorkspaceIntegration } from '../hooks/useGoogleWorkspaceIntegration'
import { combineGoogleWorkspaceStatus } from '../lib/status'
import type { IntegrationSummary } from '@/features/integrations/types'

const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4c-7.4 0-13.8 4.2-17.1 10.3z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.6l-6.5-5.5C29.6 34.8 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5c3.3 6.2 9.7 11 17.8 11z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.5 5.5C40.5 36.5 44 30.9 44 24c0-1.3-.1-2.7-.4-3.5z" />
  </svg>
)

type Props = {
  workspaceId: string
  gmailSummary: IntegrationSummary | undefined
  calendarSummary: IntegrationSummary | undefined
  onRefresh: () => void
}

const GoogleWorkspaceIntegrationCard: React.FC<Props> = ({ workspaceId, gmailSummary, calendarSummary, onRefresh }) => {
  const {
    connecting,
    disconnecting,
    showExplainer,
    openConnectModal,
    closeConnectModal,
    confirmConnect,
    disconnect,
  } = useGoogleWorkspaceIntegration(workspaceId, onRefresh)

  const status = combineGoogleWorkspaceStatus(gmailSummary, calendarSummary)
  const identifier = gmailSummary?.identifier ?? calendarSummary?.identifier
  // Appended to the connected-detail line as a tag so the Settings mailbox is
  // visibly the main one, mirroring the "Main" tag the Campaigns page shows.
  const connectedDetail = identifier && status === 'active' ? `${identifier}  ·  Main` : identifier

  const handleDisconnect = async () => {
    await disconnect()
    onRefresh()
  }

  return (
    <>
      <IntegrationCard
        icon={<GoogleIcon />}
        name="Google Workspace"
        description="Gmail for email and Google Calendar for scheduling — one connection, one consent."
        status={status}
        connectedDetail={connectedDetail}
        connecting={connecting}
        disconnecting={disconnecting}
        onConnect={openConnectModal}
        onDisconnect={handleDisconnect}
        connectLabel="Connect Workspace"
        disconnectConfirmTitle="Disconnect Google Workspace"
        disconnectConfirmMessage={`Remove the Google Workspace integration for ${identifier ?? 'this account'}? Zeph will stop reading/sending email and reading your calendar immediately.`}
      />

      {showExplainer && (
        <GoogleWorkspaceConnectModal
          connecting={connecting}
          onConfirm={confirmConnect}
          onCancel={closeConnectModal}
        />
      )}
    </>
  )
}

export default React.memo(GoogleWorkspaceIntegrationCard)
